import React, { useMemo, useState, useRef, useEffect } from "react";
import { Search, X } from "lucide-react";

import MultiSelectFilter from "@/components/leads/MultiSelectFilter";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SELECT_OPERATORS = [
  { value: "is", label: "is" },
  { value: "is_not", label: "is not" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
];

const TEXT_OPERATORS = [
  { value: "is", label: "is" },
  { value: "isnt", label: "isn't" },
  { value: "contains", label: "contains" },
  { value: "not_contains", label: "doesn't contain" },
  { value: "starts_with", label: "starts with" },
  { value: "ends_with", label: "ends with" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
];

const DATE_OPERATORS = [
  { value: "on", label: "On" },
  { value: "between", label: "Between" },
  { value: "before", label: "Before" },
  { value: "after", label: "After" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "this_year", label: "This Year" },
  { value: "last_week", label: "Last Week" },
  { value: "last_month", label: "Last Month" },
  { value: "last_year", label: "Last Year" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "last_60_days", label: "Last 60 Days" },
  { value: "last_90_days", label: "Last 90 Days" },
  { value: "ytd", label: "YTD" },
];

const toTestId = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "field";

function isValueRequired(op) {
  return !["is_empty", "is_not_empty"].includes(op);
}

function dateNeedsValue(op) {
  return ["on", "between", "before", "after"].includes(op);
}

export default function ZohoFilterPanel({
  open,
  title = "Filter Leads by",
  fields = [],
  optionsByField = {},
  draft = {},
  onChangeDraft,
  onApply,
  onClear,
}) {
  const [q, setQ] = useState("");
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Focus input when search expands
  useEffect(() => {
    if (searchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchExpanded]);

  // Close search on click outside (only if empty)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        if (!q) {
          setSearchExpanded(false);
        }
      }
    };

    if (searchExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [searchExpanded, q]);

  // Handle Escape key
  const handleSearchKeyDown = (e) => {
    if (e.key === "Escape" && !q) {
      setSearchExpanded(false);
    }
  };

  const handleClearSearch = () => {
    setQ("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const filteredFields = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return fields;
    return fields.filter((f) => String(f.label || f.key).toLowerCase().includes(s));
  }, [fields, q]);

  if (!open) return null;

  return (
    <div data-testid="zoho-filter-panel" className="flex h-full min-h-0 flex-col text-slate-100">
      {/* Header row with title and collapsible search */}
      <div ref={searchContainerRef} className="px-4 py-3">
        {!searchExpanded ? (
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">{title}</div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSearchExpanded(true)}
              aria-label="Open search"
              data-testid="zoho-filter-search-icon"
              className="h-8 w-8"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300/70 pointer-events-none" />
            <Input
              ref={searchInputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search"
              className="pl-9 pr-8 bg-white/5 text-slate-100 ring-1 ring-white/10"
              data-testid="zoho-filter-search"
            />
            {q && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-200 transition-colors"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Divider line */}
      <div className="border-t border-white/10" />

      <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
        <div className="space-y-2">
          {filteredFields.map((f) => {
            const safeKey = toTestId(f.key || f.label);
            const fieldTestId = `zoho-filter-${safeKey}`;
            const row = draft[f.key] || {};
            const enabled = Boolean(row.enabled);
            const type = f.type || "text";
            const operator = row.operator || (type === "select" ? "is" : type === "date" ? "on" : "contains");

            return (
              <div
                key={f.key}
                className="rounded-xl bg-white/5 px-2 py-3 ring-1 ring-white/10 hover:bg-white/10 hover:ring-white/15"
                data-testid={`${fieldTestId}-row`}
              >
                <label className="flex items-center gap-3">
                  <Checkbox
                    checked={enabled}
                    onCheckedChange={(v) => {
                      onChangeDraft((prev) => {
                        const next = { ...(prev || {}) };
                        if (!v) {
                          delete next[f.key];
                          return next;
                        }
                        next[f.key] = {
                          enabled: true,
                          operator: type === "select" ? "is" : type === "date" ? "on" : "contains",
                          value: "",
                          value_to: "",
                          values: [],
                        };
                        return next;
                      });
                    }}
                    className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                    data-testid={`${fieldTestId}-toggle`}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm text-slate-100" data-testid={`${fieldTestId}-label`}>
                      {f.label || f.key}
                    </div>
                  </div>
                </label>

                {enabled ? (
                  <div className="mt-3 space-y-2">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Select
                        value={operator}
                        onValueChange={(op) => {
                          onChangeDraft((prev) => {
                            const next = { ...(prev || {}) };
                            const cur = next[f.key] || { enabled: true };
                            next[f.key] = {
                              ...cur,
                              operator: op,
                              value: isValueRequired(op) ? cur.value || "" : "",
                              value_to: op === "between" ? cur.value_to || "" : "",
                              values: isValueRequired(op) ? cur.values || [] : [],
                            };
                            return next;
                          });
                        }}
                      >
                        <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid={`${fieldTestId}-operator`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="glass border-white/10">
                          {(type === "select" ? SELECT_OPERATORS : type === "date" ? DATE_OPERATORS : TEXT_OPERATORS).map((op) => (
                            <SelectItem key={op.value} value={op.value} data-testid={`${fieldTestId}-operator-${op.value}`}>
                              {op.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {type === "date" ? (
                        dateNeedsValue(operator) ? (
                          operator === "between" ? (
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                type="date"
                                value={row.value || ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  onChangeDraft((prev) => ({
                                    ...(prev || {}),
                                    [f.key]: { ...(prev?.[f.key] || {}), enabled: true, operator, value: v },
                                  }));
                                }}
                                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                                data-testid={`${fieldTestId}-date-from`}
                              />
                              <Input
                                type="date"
                                value={row.value_to || ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  onChangeDraft((prev) => ({
                                    ...(prev || {}),
                                    [f.key]: { ...(prev?.[f.key] || {}), enabled: true, operator, value_to: v },
                                  }));
                                }}
                                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                                data-testid={`${fieldTestId}-date-to`}
                              />
                            </div>
                          ) : (
                            <Input
                              type="date"
                              value={row.value || ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                onChangeDraft((prev) => ({
                                  ...(prev || {}),
                                  [f.key]: { ...(prev?.[f.key] || {}), enabled: true, operator, value: v },
                                }));
                              }}
                              className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                              placeholder="Date"
                              data-testid={`${fieldTestId}-date`}
                            />
                          )
                        ) : (
                          <div className="text-xs text-slate-200/60 sm:pt-2">No value needed</div>
                        )
                      ) : isValueRequired(operator) ? (
                        type === "select" ? (
                          <MultiSelectFilter
                            options={optionsByField[f.key] || []}
                            values={row.values || []}
                            onChange={(vals) => {
                              onChangeDraft((prev) => ({
                                ...(prev || {}),
                                [f.key]: { ...(prev?.[f.key] || {}), enabled: true, operator, values: vals },
                              }));
                            }}
                            placeholder="Select…"
                            testIdPrefix={`${fieldTestId}-multi`}
                          />
                        ) : (
                          <Input
                            type="text"
                            value={row.value || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              onChangeDraft((prev) => ({
                                ...(prev || {}),
                                [f.key]: { ...(prev?.[f.key] || {}), enabled: true, operator, value: v },
                              }));
                            }}
                            className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                            placeholder="Value"
                            data-testid={`${fieldTestId}-value`}
                          />
                        )
                      ) : (
                        <div className="text-xs text-slate-200/60 sm:pt-2">No value needed</div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div data-testid="filters-footer" className="h-[60px] border-t border-white/10 px-4 flex items-center">
        <div className="flex w-full items-center justify-between gap-2">
          <button
            type="button"
            className="h-9 rounded-full bg-white/10 px-4 text-sm font-medium text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
            onClick={onClear}
            data-testid="zoho-filter-clear-button"
          >
            Clear
          </button>
          <button
            type="button"
            className="h-9 rounded-full bg-indigo-500/20 px-4 text-sm font-medium text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
            onClick={onApply}
            data-testid="zoho-filter-apply-button"
          >
            Apply Filter
          </button>
        </div>
      </div>
    </div>
  );
}
